import type { ICellRendererComp, ICellRendererParams } from "ag-grid-enterprise";

interface ToggleCheckboxCellRendererParams extends ICellRendererParams {
  // Row-data field holding the PATCH target. A row with no URL renders the box disabled.
  urlField?: string;
  // Row-data field holding a per-row disable reason. Present => disabled, and used as the title.
  disabledField?: string;
  // Cell value that means "checked". Defaults to "Yes".
  checkedValue?: string;
  // Cell value that means "unchecked". Defaults to "No".
  uncheckedValue?: string;
}

/**
 * ToggleCheckboxCellRenderer — renders a boolean cell as a checkbox that PATCHes `value=true|false`
 * to a per-row URL and updates the row in place, with no full-page reload. Only a 2xx commits; on
 * failure the box reverts to what the server still holds. Rows with a `disabledField` reason (or
 * no URL) render read-only.
 *
 * Reads:
 * - `params.value` — current state, compared against checkedValue/uncheckedValue
 * - `params.data[params.urlField ?? "toggle_url"]` — the PATCH target
 * - `params.data[params.disabledField]` — when present, why this row is locked
 */
export class ToggleCheckboxCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;
  private input!: HTMLInputElement;
  private params!: ToggleCheckboxCellRendererParams;
  private url: string | null = null;
  private onChange = (): void => {
    void this.persist();
  };

  init(params: ToggleCheckboxCellRendererParams): void {
    this.params = params;
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-toggle-checkbox-cell";

    if (params.node.group) return;

    const data = params.data as Record<string, unknown> | undefined;
    this.url = (data?.[params.urlField ?? "toggle_url"] as string | undefined) ?? null;
    const disabledReason = params.disabledField ? (data?.[params.disabledField] as string | undefined) : undefined;

    // The global form CSS hides bare checkboxes and draws the box with the sibling label's
    // ::before, so this needs the same input+label pair check_box_tag builds elsewhere.
    const inputId = `ag-toggle-${params.colDef?.field ?? "cell"}-${params.node.id ?? params.node.rowIndex}`;

    this.input = document.createElement("input");
    this.input.type = "checkbox";
    this.input.id = inputId;
    this.input.checked = params.value === this.checkedValue();
    this.input.disabled = !this.url || Boolean(disabledReason);

    const label = document.createElement("label");
    label.htmlFor = inputId;
    // On the wrapper, not the label: a disabled checkbox's label gets `pointer-events: none`,
    // so a title there never fires a tooltip.
    if (disabledReason) this.eGui.title = disabledReason;

    if (!this.input.disabled) this.input.addEventListener("change", this.onChange);
    this.eGui.appendChild(this.input);
    this.eGui.appendChild(label);
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  // Re-init instead: the checked state comes from the row data, which the refresh would race.
  refresh(): boolean {
    return false;
  }

  destroy(): void {
    this.input?.removeEventListener("change", this.onChange);
  }

  private checkedValue(): string {
    return this.params.checkedValue ?? "Yes";
  }

  private uncheckedValue(): string {
    return this.params.uncheckedValue ?? "No";
  }

  private async persist(): Promise<void> {
    const desired = this.input.checked;
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";

    this.input.disabled = true;
    try {
      const response = await fetch(`${this.url}?value=${desired}`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrf,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);

      // Order matters: record the lock BEFORE #commit, whose setDataValue re-renders the cell
      // and replaces this instance. Written after, it lands on a detached node.
      this.recordLock(await this.lockedReasonFrom(response));
      this.commit(desired);
    } catch {
      // Server still holds the old value, so put the box back rather than show a lie.
      this.input.checked = !desired;
      this.input.disabled = false;
      alert("Failed to update this setting. Please try again.");
    }
  }

  // A response without a JSON body, or without the key, leaves the row unlocked.
  private async lockedReasonFrom(response: Response): Promise<string | null> {
    try {
      const body = (await response.json()) as { locked?: string | null };
      return body?.locked ?? null;
    } catch {
      return null;
    }
  }

  private recordLock(reason: string | null): void {
    const lockField = this.params.disabledField;
    if (!lockField || !this.params.data) return;

    (this.params.data as Record<string, unknown>)[lockField] = reason;
  }

  // Writes through to the row node so a re-render (scroll, sort) keeps the new state.
  private commit(desired: boolean): void {
    const field = this.params.colDef?.field;
    if (!field || !this.params.data) return;

    (this.params.data as Record<string, unknown>)[field] = desired ? this.checkedValue() : this.uncheckedValue();
    this.params.node.setDataValue(field, desired ? this.checkedValue() : this.uncheckedValue());
  }
}
